import { useEffect, useRef, useState } from "react";
import { cx } from "../../utils/cx";

/**
 * Click-to-open menu. `trigger` is the button content; children render inside the panel
 * (use <DropdownItem> for rows); any click inside the panel closes it. `fixed` places the panel
 * against the window, so a scrolling container such as a table does not cut it off.
 */
export function Dropdown({ trigger, label, align = "end", fixed = false, className, triggerClassName, panelClassName, children }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null); // where a fixed panel goes
  const ref = useRef(null);

  const toggle = () => {
    if (fixed && !open) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 8, ...(align === "end" ? { right: window.innerWidth - rect.right } : { left: rect.left }) });
    }
    setOpen((o) => !o);
  };

  // A fixed panel would drift from its trigger on scroll or resize, so it closes instead
  useEffect(() => {
    if (!open || !fixed) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, fixed]);

  // Outside clicks and Escape close the menu
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cx("relative", className)}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={toggle} className={triggerClassName}>
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          onClick={() => setOpen(false)}
          className={cx(
            "z-50 min-w-56 rounded-lg border border-slate-100 bg-white py-1 shadow-lg",
            fixed ? "fixed" : cx("absolute top-full mt-2", align === "end" ? "right-0" : "left-0"),
            panelClassName
          )}
          style={fixed ? position : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function DropdownItem({ icon, danger = false, className, children, ...props }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cx(
        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium",
        danger ? "text-red-600 hover:bg-red-50 hover:text-red-700" : "text-slate-700 hover:bg-slate-50",
        className
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
}
